import apiClient from './axios.config';
import {
  WeaponDetailDto,
  ArmorDetailDto,
  ItemDetailDto,
  NpcDetailDto,
  SkillDetailDto,
  WikiSearchResponseDto,
  WikiSearchSortBy
} from '../types';

// Types for new wiki architecture
export interface WikiTypesResponse {
  categories: WikiCategory[];
}

export interface WikiCategory {
  id: number;
  name: string;
  source: string;
  subCategories?: WikiSubCategory[];
  races?: WikiRace[];
}

export interface WikiSubCategory {
  id: number;
  name: string;
  source: string;
  types: WikiType[];
}

export interface WikiType {
  id: number;
  name: string;
  source?: string;
  subTypes: WikiSubType[];
}

export interface WikiSubType {
  id: number;
  name: string;
  source?: string;
}

export interface WikiRace {
  id: number;
  name: string;
  source?: string;
  skillTypes: WikiSkillType[];
}

export interface WikiSkillType {
  id: number;
  name: string;
  source?: string;
  classes: WikiClass[];
}

export interface WikiClass {
  id: number;
  name: string;
  source?: string;
  professions: WikiProfession[];
}

export interface WikiProfession {
  id: number;
  name: string;
  source?: string;
  parentProfessionId?: number | null;
}

// Generic filters for all source types
export interface WikiItemFilters {
  // Type/subtype filtering
  typeId?: number;
  subtypeId?: number;

  // Common filters
  rank?: string;
  crystalType?: string;
  wikiItemLevel?: number;

  // NPC specific
  wikiSubCategory?: number;
  level?: number;

  // Skill specific (ID-based parameters)
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
  operatorType?: string;

  // Search and pagination
  search?: string;
  sortBy?: string;
  page: number;
  size: number;
}

// Generic paginated response
export interface PaginatedWikiResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Generic wiki item (for list views)
export interface WikiItem {
  id: number;
  name: string;
  icon: string;
  iconPanel: string;
  grade?: string;
  wikiType?: string;
  wikiSubType?: string;
  level?: number;
  rank?: string;
  // Add other common fields as needed
}

export const wikiNewApi = {
  /**
   * Get all wiki categories and types structure
   * This loads the complete navigation structure from wiki/types
   */
  getWikiTypes: async (): Promise<WikiTypesResponse> => {
    const response = await apiClient.get<WikiTypesResponse>('/wiki/types');
    return response.data;
  },

  /**
   * Generic method to load items from any source with filters
   * @param source - The API source (weapons, armors, items, npcs, skills)
   * @param filters - Filters specific to the source type
   */
  getItemsBySource: async (
    source: string,
    filters: WikiItemFilters
  ): Promise<PaginatedWikiResponse<WikiItem>> => {
    const params = new URLSearchParams();

    // Add type/subtype filters (RESTful ID-based)
    if (filters.typeId) params.append('wikiType', filters.typeId.toString());
    if (filters.subtypeId) params.append('wikiSubType', filters.subtypeId.toString());

    // Add common filters
    if (filters.rank) params.append('rank', filters.rank);
    if (filters.crystalType) params.append('crystalType', filters.crystalType);
    if (filters.wikiItemLevel) params.append('wikiItemLevel', filters.wikiItemLevel.toString());

    // Add NPC-specific filters
    if (filters.wikiSubCategory) params.append('wikiSubCategory', filters.wikiSubCategory.toString());
    if (filters.level) params.append('level', filters.level.toString());

    // Add skill-specific filters (using ID-based parameters)
    if (filters.raceId) params.append('raceId', filters.raceId.toString());
    if (filters.skillTypeId) params.append('skillTypeId', filters.skillTypeId.toString());
    if (filters.classId) params.append('classId', filters.classId.toString());
    if (filters.professionId) params.append('professionId', filters.professionId.toString());
    if (filters.operatorType) params.append('operatorType', filters.operatorType);

    // Add search and pagination
    if (filters.search) params.append('name', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    params.append('page', filters.page.toString());
    params.append('size', filters.size.toString());

    const response = await apiClient.get<PaginatedWikiResponse<WikiItem>>(
      `/wiki/${source}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Search items from any source
   * @param source - The API source
   * @param query - Search query
   * @param filters - Additional filters
   */
  searchItemsBySource: async (
    source: string,
    query: string,
    filters: Partial<WikiItemFilters>
  ): Promise<PaginatedWikiResponse<WikiItem>> => {
    const searchFilters: WikiItemFilters = {
      search: query,
      page: filters.page || 0,
      size: filters.size || 12,
      sortBy: filters.sortBy || 'name',
      ...filters
    };

    return wikiNewApi.getItemsBySource(source, searchFilters);
  },

  /**
   * Global wiki search by name across items (weapons/armors/items), skills and NPCs.
   * Каждый результат несёт source+id — их достаточно, чтобы открыть карточку /wiki/{source}/details/{id}.
   * @param query - строка запроса (2..64 символа)
   * @param source - опциональный фильтр по главной группе: 'items' | 'skills' | 'npcs' (иначе ищет везде).
   *                 Форма ответа та же ({items, skills, npcs}) — просто заполнена только выбранная группа.
   */
  globalSearch: async (
    query: string,
    source?: 'items' | 'skills' | 'npcs',
    sortBy: WikiSearchSortBy = 'name'
  ): Promise<WikiSearchResponseDto> => {
    const response = await apiClient.get<WikiSearchResponseDto>('/wiki/search', {
      params: {
        query,
        ...(source ? { source } : {}),
        sortBy,
      },
    });
    return response.data;
  },

  /**
   * Get detailed weapon information by ID
   * @param id - Weapon ID
   */
  getWeaponDetail: async (id: number): Promise<WeaponDetailDto> => {
    const response = await apiClient.get<WeaponDetailDto>(`/wiki/weapons/${id}`);
    return response.data;
  },

  /**
   * Get detailed armor information by ID
   * @param id - Armor ID
   */
  getArmorDetail: async (id: number): Promise<ArmorDetailDto> => {
    const response = await apiClient.get<ArmorDetailDto>(`/wiki/armors/${id}`);
    return response.data;
  },

  /**
   * Get detailed item information by ID
   * @param id - Item ID
   */
  getItemDetail: async (id: number): Promise<ItemDetailDto> => {
    const response = await apiClient.get<ItemDetailDto>(`/wiki/items/${id}`);
    return response.data;
  },

  /**
   * Get detailed NPC information by ID
   * @param id - NPC ID
   */
  getNpcDetail: async (id: number): Promise<NpcDetailDto> => {
    const response = await apiClient.get<NpcDetailDto>(`/wiki/npcs/${id}`);
    return response.data;
  },

  /**
   * Get detailed skill information by skill ID
   * @param skillId - Skill ID
   */
  getSkillDetail: async (skillId: number): Promise<SkillDetailDto[]> => {
    const response = await apiClient.get<SkillDetailDto[]>(`/wiki/skills/${skillId}`);
    return response.data;
  },

  /**
   * Get skill detail by class (when race, type, class, profession are available)
   * @param skillId - Skill ID
   * @param raceId - Race ID
   * @param skillTypeId - Skill Type ID
   * @param classId - Class ID
   * @param professionId - Profession ID
   */
  getSkillDetailByClass: async (
    skillId: number,
    raceId: number,
    skillTypeId: number,
    classId: number,
    professionId: number
  ): Promise<SkillDetailDto[]> => {
    const response = await apiClient.get<SkillDetailDto[]>(`/wiki/skills/classes/${skillId}`, {
      params: { raceId, skillTypeId, classId, professionId }
    });
    return response.data;
  },

  /**
   * Generic method to get item detail based on source type (for backwards compatibility)
   * @param source - The API source (weapons, armors, items, npcs, skills)
   * @param id - Item ID
   */
  getItemDetailBySource: async (source: string, id: number): Promise<WeaponDetailDto | ArmorDetailDto | ItemDetailDto | NpcDetailDto | SkillDetailDto[]> => {
    switch (source) {
      case 'weapons':
        return wikiNewApi.getWeaponDetail(id);
      case 'armors':
      case 'accessories':
      case 'jewelry':
        return wikiNewApi.getArmorDetail(id);
      case 'items':
        return wikiNewApi.getItemDetail(id);
      case 'npcs':
        return wikiNewApi.getNpcDetail(id);
      case 'skills':
        return wikiNewApi.getSkillDetail(id);
      default:
        throw new Error(`Неподдерживаемый источник: ${source}`);
    }
  },

  /**
   * Determine source type based on item type or category
   * This is a utility function to help with drill-down navigation
   */
  determineSourceFromItemType: (itemType: string, category?: string): string => {
    // Map item types to sources
    const typeToSourceMap: { [key: string]: string } = {
      // Weapons
      'Мечи': 'weapons',
      'Кинжалы': 'weapons',
      'Луки': 'weapons',
      'Древковое': 'weapons',
      'Тупое': 'weapons',
      'Двуручное': 'weapons',
      'Заточка': 'weapons',
      'Дуальный': 'weapons',

      // Armors
      'Легкая': 'armors',
      'Тяжелая': 'armors',
      'Роба': 'armors',
      'Сигил': 'armors',

      // Accessories
      'Серьги': 'accessories',
      'Кольца': 'accessories',
      'Ожерелья': 'accessories',

      // Jewelry
      'Левые браслеты': 'jewelry',
      'Правые браслеты': 'jewelry',
      'Талисманы': 'jewelry',

      // Items
      'Руны': 'items',
      'Свитки зачарования': 'items',
      'Ресурсы': 'items'
    };

    return typeToSourceMap[itemType] || 'items';
  },

  /**
   * Get available sort options for a specific source
   */
  getSortOptionsForSource: (_source: string) => {
    return [
      { value: 'name', label: 'Название (А-Я)' },
      { value: 'name_desc', label: 'Название (Я-А)' }
    ];
  },

  /**
   * Get unique NPC levels by subcategory
   * @param wikiSubCategory - Subcategory ID
   * @returns Array of unique levels
   */
  getNpcLevels: async (wikiSubCategory: number): Promise<number[]> => {
    const response = await apiClient.get<number[]>('/wiki/npcs/levels', {
      params: { wikiSubCategory }
    });
    return response.data;
  }
};